import React, {useState, useEffect, useRef} from "react";
import {
  Menu,
  TextInput,
  UnstyledButton,
  Flex,
  Text,
  Box,
  Group,
  Divider,
  Accordion, ActionIcon, Stack, Highlight, Avatar, createStyles
} from "@mantine/core";
import {
  IconCheck,
  IconCircleCheck,
  IconCopy,
  IconLogout,
  IconSearch,
  IconSettings,
  IconSwitch2,
  IconX
} from "@tabler/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { isBagelAdmin, isBagelSuperUser, logout } from "../../../utils";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import {Link, useLocation} from "react-router-dom";
import {BagelIcon, DraggableChatModal} from "../../chat";
import {usePostHog} from "posthog-js/react";
import {useGlobalAccounts} from "../../../hooks/useGlobalAccounts";
import {useGlobalUserAccountUpdate} from "../../../hooks/useGlobalUserAccountUpdate";
import {CLASS_NAMES} from "../Header/constants";
import {getInitials} from "../../../utils/getInitials";
import {closeAllModals, openConfirmModal} from "@mantine/modals";
import { sidebarFooterTargetClass, sidebarFooterLabelClass } from "./utils";
import "../ProfileMenuShared.css";
import { useUserPermissions } from "../../../hooks/useUserPermissions";

const useStyles = createStyles(() => ({
  link: {
    marginTop: "2px",
    marginBottom: "2px",
    borderRadius: "8px",
    padding: "8px",
    display: "flex",
    fontSize: "14px",
    textDecoration: "none",
    color: "#161832",
    fontWeight: 500,
    lineHeight: "20px",
    height: "36px",
    width: "100%",
    backgroundColor: "transparent",
    transition: "background-color 0.15s ease",

    "&:hover": {
      backgroundColor: "#F0F0F0",
    },
  },

  linkIcon: {
    color: "#161832",
  },

  linkActive: {
    color: "#5b60e3",
    borderRadius: "8px",
    backgroundColor: "#EAEAFE",
    "&:hover": {
      backgroundColor: "#EAEAFE",
    },
    // linkIcon forces #161832; keep icons aligned with active label (same as Tabler `color` on settings)
    "& $linkIcon": {
      color: "#5b60e3",
    },
    /** Profile menu: row hover uses Mantine gray; keep active tint + brand text */
    "[data-menu-item][data-hovered] &": {
      color: "#5b60e3",
      backgroundColor: "#EAEAFE",
      "& $linkIcon": {
        color: "#5b60e3",
      },
    },
  },

  /** Profile Menu.Dropdown rows: full-width hover comes from Menu.Item; inner stays flat; brand color on hover */
  menuDropdownLink: {
    marginTop: 0,
    marginBottom: 0,
    height: "auto",
    minHeight: 36,
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    columnGap: 12,
    backgroundColor: "transparent",
    borderRadius: 0,
    transition: "color 0.15s ease",
    "&:hover": {
      backgroundColor: "transparent",
    },
    "& $linkIcon": {
      flexShrink: 0,
    },
    "[data-menu-item][data-hovered] &": {
      color: "#5b60e3",
      "& $linkIcon": {
        color: "#5b60e3",
      },
    },
  },

  /** Bagel mark stays orange on row hover (label still turns brand per design) */
  menuDropdownBagelSlot: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }
}));

const ProfileMenu = ({ sidebarExpanded, onOpenChange }: { sidebarExpanded: boolean; onOpenChange?: (opened: boolean) => void }) => {
  const { pathname } = useLocation();
  const auth0 = useAuth0();
  const posthog = usePostHog();

  const { canAccessSettings } = useUserPermissions();
  const { classes, cx } = useStyles();
  const [currentAccount, setCurrentAccount] = useState<any>();
  const [accounts, setAccounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccountIndex, setSelectedAccountIndex] = useState<any | null>(null);
  const [copiedAccountId, setCopiedAccountId] = useState<string | null>(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const accountSearchRef = useRef<HTMLInputElement>(null);

  const { data: accountsData } = useGlobalAccounts();
  const globalUserAccountUpdate = useGlobalUserAccountUpdate();
  const user = auth0.user;
  const name = user ? user.name : '';
  const userFooterDisplayName =
    user && (user.given_name != null || user.family_name != null)
      ? [user.given_name, user.family_name].filter(Boolean).join(" ")
      : (name || "");
  const currentUser = useCurrentUser();
  const userId = currentUser?.id;
  const role = currentUser?.role ?? '';
  const userAccountId = currentUser?.accountId ?? '';

  useEffect(() => {
    if (accountsData) {
      setAccounts(accountsData);
      fetchCurrentAccount(accountsData, userAccountId);
    }
  }, [accountsData, userAccountId, user]);

  function fetchCurrentAccount(accounts: any, accountId: any) {
    const currentAccountDetails = accounts.filter((value: { _id: string, name: string }) =>
      value._id == accountId
    )
    setCurrentAccount(currentAccountDetails[0]);
  }

  const avatar = user?.picture ? (<Avatar src={user.picture} radius="xl" className={CLASS_NAMES.userAvatar} />) : (<Avatar color="cyan" radius="xl" className={CLASS_NAMES.userAvatar}>
    {name && getInitials(name)}
  </Avatar>)

  function handleAccountChange(_id: any) {
    openConfirmModal({
      title: <Text fz={20} fw={600} p="sm" >Switch account</Text>,
      size: 496,
      centered: true,
      children: (
        <Text size="sm" mb={30}>Are you sure you want to switch account?</Text>
      ),
      styles: {
        close: {
          alignSelf: "flex-start",
          marginTop: 10,
          marginRight: 10,
        },
        header: {
          padding: "0px",
        },

      },
      labels: { confirm: "Yes", cancel: "No" },
      confirmProps: {
        color: "red",
        w: 100,
        px: 8,
        styles: {
          label: {
            fontSize: 12,
            fontWeight: 500
          },
        },
        leftIcon: <IconCheck size={16} />,
      },
      cancelProps: {
        px: 8,
        w: 100,
        styles: {
          label: {
            fontSize: 12,
            fontWeight: 500
          },
        },
        leftIcon: <IconX size={14} />,
      },
      onClose: () => {
        closeAllModals();
      },
      onConfirm: () => {
        globalUserAccountUpdate.mutate(
          { userId, newAccountId: _id },
          {
            onSuccess: () => {
              fetchCurrentAccount(accounts, _id);
              posthog?.capture('bagel_account_switched', {
                switchedToAccount: accounts?.find((account: any) => account._id === _id),
              });
              window.location.reload();
              localStorage.clear();
            },
            onError: (e: any) => console.log(e),
          }
        );
      }
    });
  }

  return (
    <>
      <Menu
        width={300}
        shadow="md"
        position={"left-end"}
        withArrow
        withinPortal
        onOpen={() => onOpenChange?.(true)}
        onClose={() => onOpenChange?.(false)}
      >
        <Menu.Target>
          <UnstyledButton p={0} className={sidebarFooterTargetClass(sidebarExpanded)}>
            <Flex align="center" w="100%">
              <Box className="sidebar-footer-icon-slot">
                {avatar}
              </Box>
              <Box ml="md" className={sidebarFooterLabelClass(sidebarExpanded)} style={{ minWidth: 0 }}>
                <Text
                  fz="14px"
                  mb="1px"
                  lineClamp={1}
                >
                  {userFooterDisplayName}
                </Text>
                {currentAccount?.name && (
                  <Text fz="12px" color="dimmed" lineClamp={1}>
                    {currentAccount.name}
                  </Text>
                )}
              </Box>
            </Flex>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown p={0}>
          <Group p={"md"}>
            {avatar}
            <Box>
              <Text transform="capitalize">{name}</Text>
              <Text transform="capitalize" size={"xs"}>{role} | <Text span>{currentAccount?.name}</Text> </Text>
            </Box>
          </Group>
          <Divider />
          {isBagelSuperUser(currentUser) && (
            <Accordion transitionDuration={0} onChange={(value) => {
              if (value) accountSearchRef.current?.focus();
            }} styles={{
              content: {
                padding: '0px',
              }
            }}>
              <Accordion.Item value="bagelAccounts">
                <Accordion.Control icon={<IconSwitch2 size={20} />} onClick={(e) => {e.stopPropagation(); setSearchQuery("");}}>
                  <Text fz="sm">Change account</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <TextInput
                    ref={accountSearchRef}
                    className="profile-menu-account-input"
                    px="sm"
                    mb={10}
                    radius="md"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.currentTarget.value)}
                    onClick={(e) => e.stopPropagation()}
                    icon={<IconSearch size={14.5} />}
                    rightSection={
                      searchQuery.length > 0 && (
                        <ActionIcon
                          variant="transparent"
                          onClick={(e) => {setSearchQuery(""); e.stopPropagation();}}
                          style={{ pointerEvents: "all" }}
                        >
                          <IconX size={14.5} />
                        </ActionIcon>
                      )
                    }
                  />
                  <Box
                    px="xs"
                    mb="sm"
                    className="profile-menu-account-scroll"
                  >
                    <Stack spacing="xs">
                      {accounts.filter((account: any) =>
                        account.name.toLowerCase().includes(searchQuery.toLowerCase())
                      ).map((account: any, index) => (
                        <Text
                          key={index}
                          className={cx(
                            "profile-menu-account-row",
                            selectedAccountIndex === index && "profile-menu-account-row--selected"
                          )}
                          onClick={() => { setSelectedAccountIndex(index); handleAccountChange(account._id); }}>
                          <Group>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text> <Highlight highlight={searchQuery}>{account.name}</Highlight></Text>
                              <Group spacing={4} noWrap>
                                <Text size={"xs"} color="dimmed">{account._id}</Text>
                                <ActionIcon
                                  className="profile-menu-copy-btn"
                                  size="xs"
                                  variant="transparent"
                                  onClick={async (e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    try {
                                      await navigator.clipboard.writeText(account._id);
                                      setCopiedAccountId(account._id);
                                      setTimeout(() => setCopiedAccountId((prev) => prev === account._id ? null : prev), 1500);
                                    } catch {
                                      // clipboard write failed (e.g. permissions denied)
                                    }
                                  }}
                                >
                                  {copiedAccountId === account._id
                                    ? <IconCheck size={12} color="#5B60E3" />
                                    : <IconCopy size={12} />}
                                </ActionIcon>
                              </Group>
                            </Box>
                            {
                              userAccountId === account._id &&
                                <IconCircleCheck color="#5B60E3" size={16} />
                            }
                          </Group>
                        </Text>
                      ))}
                    </Stack>
                  </Box>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}
          {canAccessSettings && (
            <Menu.Item p={0}>
              <Link
                to={"/settings"}
                className={cx(classes.link, classes.menuDropdownLink, {
                  [classes.linkActive]: "/settings" === pathname,
                })}
              >
                <IconSettings className={classes.linkIcon} stroke={1.5} />
                <span>Settings</span>
              </Link>
            </Menu.Item>
          )}
          {isBagelAdmin(currentUser) && (
            <Menu.Item p={0}>
              <a
                href="#"
                className={cx(classes.link, classes.menuDropdownLink)}
                onClick={(e) => {
                  e.preventDefault();
                  setIsChatModalOpen(true);
                }}
              >
                <Box className={classes.menuDropdownBagelSlot}>
                  <BagelIcon
                    height={16}
                    width={16}
                    bagelFill="#FF8700"
                    backgroundFill="transparent"
                  />
                </Box>
                <span>Account Setup Agent</span>
              </a>
            </Menu.Item>
          )}
          <Divider />
          <Menu.Item p={0}>
            <a
              href="#"
              className={cx(classes.link, classes.menuDropdownLink)}
              onClick={(e) => {
                e.preventDefault();
                posthog?.capture('logged_out')
                logout(auth0);
              }}
            >
              <IconLogout className={classes.linkIcon} stroke={1.5} />
              <span>Logout</span>
            </a>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <DraggableChatModal
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
      />
    </>
  );
};

export default ProfileMenu;
